/**
 * Text rendering for the byte drawer (REQ-UI-185).
 *
 * Pure: turns a hex window into the *complete* text it decodes to, and
 * pretty-prints that text when it is structured. Separated from the
 * drawer component so the decoding rules are unit-testable without a
 * render — the rules are the requirement, not the markup.
 *
 * # Why a text column in addition to the hex dump's ASCII gutter
 *
 * The gutter is 16 characters wide and maps every non-printable byte to
 * ``.`` — the hex-dump convention, and correct there, because the gutter
 * exists to keep column alignment with the hex. But that makes it unable
 * to answer the question an operator actually asks of a stored message:
 * *what did the producer send, in words?* A SAPIENT payload's gutter
 * reads as runs of dots, which is indistinguishable from elision. So the
 * drawer carries a second rendering that accounts for **every** byte in
 * the window, wraps rather than truncates, and keeps the identity of the
 * bytes the gutter flattens.
 *
 * # The decoding rules, and why each one is what it is
 *
 * - **UTF-8, lossy.** Invalid sequences become U+FFFD and are *counted*,
 *   so the UI can say "this window is mostly binary" rather than letting
 *   a wall of replacement characters imply the data is corrupt.
 * - **Control bytes render as their Unicode control pictures**
 *   (U+2400 ␀ … U+241F ␟, U+2421 ␡), not as a dot. One column wide, so
 *   the rendering stays readable, and *identifying* — a protobuf field
 *   tag and a unit separator are different facts.
 * - **Tab, newline and carriage return survive as themselves.**
 *   Substituting them would destroy the layout of text content, which is
 *   the one thing this view exists to show.
 *
 * Cross-references:
 *   - REQ-UI-185 (complete text rendering + the formatted control).
 *   - REQ-UI-123 / REQ-UI-124 (the drawer and its fail-safe badge).
 *   - query/hex-dump.ts (the sibling renderer this complements).
 */

/** Code points below this are C0 control characters. */
const C0_CEILING = 0x20;

/** The Unicode "Control Pictures" block base — U+2400 is ␀. */
const CONTROL_PICTURE_BASE = 0x2400;

/** U+2421 ␡, the picture for DEL (U+007F). */
const DELETE_PICTURE = "␡";

/** Controls that carry layout and must survive verbatim. */
const LAYOUT_CONTROLS = new Set(["\t", "\n", "\r"]);

/**
 * Fraction of decoded characters that must be ordinary text before the
 * window is called textual. Two thirds: a JSON or XML payload clears it
 * comfortably, while a protobuf frame — tags, varints and length
 * prefixes between its string fields — does not.
 */
const TEXTUAL_THRESHOLD = 2 / 3;

/** The complete text decoding of one byte window. */
export interface DecodedText {
  /**
   * The decode. Every byte in the window is represented; nothing is
   * elided, collapsed or truncated (REQ-UI-185).
   */
  readonly text: string;
  /**
   * How many bytes were not valid UTF-8 (each rendered U+FFFD). Zero on
   * clean text; large on a binary payload — which is the honest way for
   * the UI to say "this is not a text message" without hiding the bytes.
   */
  readonly invalidByteCount: number;
  /**
   * Whether the window reads as text at all. Drives an advisory line, not
   * a decision to withhold the decode: the operator always gets the
   * rendering, and is told what kind of thing they are looking at.
   */
  readonly looksTextual: boolean;
}

/**
 * Decode a lowercase-hex byte window into its complete text rendering.
 *
 * An odd-length hex string (a truncated response) drops its trailing
 * nibble rather than inventing a byte, mirroring
 * :func:`hex-dump.hexDumpRows` — half a byte is not a fact about the
 * data.
 *
 * @param dataHex - The window as lowercase hex, exactly as the BFF serves it.
 * @returns The decode plus the two facts the UI needs to caption it.
 */
export function decodeWindowText(dataHex: string): DecodedText {
  const usable = dataHex.length - (dataHex.length % 2);
  const bytes = new Uint8Array(usable / 2);
  for (let i = 0; i < usable; i += 2) {
    bytes[i / 2] = Number.parseInt(dataHex.slice(i, i + 2), 16);
  }

  // Non-fatal: a forensic surface shows what is there and labels it, rather
  // than refusing to render bytes that are not clean UTF-8.
  const raw = new TextDecoder("utf-8", { fatal: false }).decode(bytes);

  let invalidByteCount = 0;
  let textual = 0;
  let out = "";
  for (const character of raw) {
    if (character === "�") {
      invalidByteCount += 1;
      out += character;
      continue;
    }
    const code = character.codePointAt(0) ?? 0;
    if (code < C0_CEILING && !LAYOUT_CONTROLS.has(character)) {
      out += String.fromCodePoint(CONTROL_PICTURE_BASE + code);
      continue;
    }
    if (code === 0x7f) {
      out += DELETE_PICTURE;
      continue;
    }
    textual += 1;
    out += character;
  }

  const total = [...raw].length;
  return {
    text: out,
    invalidByteCount,
    looksTextual: total > 0 && textual / total >= TEXTUAL_THRESHOLD,
  };
}

/** The structured format a text decode was rendered as. */
export type TextFormat = "json" | "xml" | "none";

/** A text decode, pretty-printed where that was possible. */
export interface FormattedText {
  /** What the content turned out to be. */
  readonly format: TextFormat;
  /** The formatted text, or the input unchanged when nothing applied. */
  readonly text: string;
  /**
   * Why no formatting was applied, or ``null`` when some was. Stated so
   * the control can be disabled **with its reason** rather than offered
   * and then quietly doing nothing — the same posture the blocked
   * download takes (REQ-UI-129).
   */
  readonly reason: string | null;
}

/**
 * Pretty-print a text decode when it is JSON or XML.
 *
 * Deliberately conservative: it formats what it can prove is structured
 * and otherwise says so. Guessing at a format and emitting a rearranged
 * approximation would be the wrong failure mode on a surface whose
 * purpose is showing the operator exactly what arrived.
 *
 * @param text - A decoded window, as :func:`decodeWindowText` produced it.
 * @returns The formatted rendering, or the input plus the reason it stands.
 */
export function formatStructuredText(text: string): FormattedText {
  const trimmed = text.trim();
  if (trimmed === "") {
    return { format: "none", text, reason: "There is no text content to format." };
  }
  if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
    try {
      return { format: "json", text: JSON.stringify(JSON.parse(trimmed), null, 2), reason: null };
    } catch {
      return {
        format: "none",
        text,
        reason: "This content starts like JSON but could not be parsed as JSON.",
      };
    }
  }
  if (trimmed.startsWith("<")) {
    return { format: "xml", text: indentXml(trimmed), reason: null };
  }
  return {
    format: "none",
    text,
    reason:
      "This content is not JSON or XML, so there is no structure to lay out. " +
      "The text above is the complete decoding of these bytes.",
  };
}

/**
 * Indent XML one element per line.
 *
 * Hand-rolled rather than routed through ``DOMParser`` for two reasons:
 * it stays pure (testable with no DOM, and identical under SSR), and it
 * re-emits the operator's own markup instead of a serializer's
 * normalization of it — on a fidelity surface, "what arrived" must not be
 * silently re-spelled. Malformed markup therefore degrades to odd
 * indentation rather than to a parser error swallowing the content.
 */
function indentXml(xml: string): string {
  // Put each tag on its own line first, then assign depth. Splitting on the
  // tag boundary (rather than regex-replacing inside tags) keeps attribute
  // values — which may themselves contain ``>`` — untouched.
  const withBreaks = xml.replace(/>\s*</g, ">\n<");
  let depth = 0;
  const lines: string[] = [];
  for (const line of withBreaks.split("\n")) {
    const token = line.trim();
    if (token === "") continue;
    const isClosing = token.startsWith("</");
    if (isClosing) depth = Math.max(0, depth - 1);
    lines.push("  ".repeat(depth) + token);
    const isDeclaration = token.startsWith("<?") || token.startsWith("<!");
    const isSelfClosing = token.endsWith("/>");
    const isSingleLineElement = /^<[^/!?][^>]*>.*<\/[^>]+>$/.test(token);
    if (!isClosing && !isDeclaration && !isSelfClosing && !isSingleLineElement) {
      depth += 1;
    }
  }
  return lines.join("\n");
}
